/**
 * @file useSiteAccessActions.ts
 * @description Действия с паузой сайта без окна (phase19.16, SB138;
 * uiux-site-closed.md §4, §8; db-design-site-closed.md §6): «Открыть сайт»
 * (в один клик, toast), «Продлить на 1 ч», «Смотреть сайт» (пропуск
 * сотрудника). Общие для плашки, раздела «Доступ к сайту» и «⋯» конструктора.
 *
 * «Смотреть сайт»: вкладка открывается синхронно по нажатию (иначе её
 * заблокирует браузер), адрес пропуска подставляется после ответа сервера;
 * ошибка — вкладка закрывается, текст в toast. Ответ мутации сразу кладётся
 * в хранилище; ошибка «состояние устарело» (409/404) — перечитываем.
 */

import { useState } from "react";

import { useNotify } from "../../components/ui/NotifyProvider";
import { siteAccess } from "../../lib/api-site-access";
import { isSiteAccessNetworkError, isSiteAccessStale, siteAccessErrorText, type SiteAccessAction } from "../../lib/site-access/site-access-errors";
import { safeTimeZone, zonedWhen } from "../../lib/site-access/site-access-time";
import type { SiteAccessHandle } from "./useSiteAccess";

/** Подпись после закрытия или открытия: витрина узнаёт о смене в течение 10 с (§7.25, вопрос 10). */
export const SITE_ACCESS_DELAY_NOTE = "изменение вступит в силу в течение 10 секунд";

export type SiteAccessBusy = "open" | "extend" | "watch" | null;

export function useSiteAccessActions(access: SiteAccessHandle) {
  const notify = useNotify();
  const [busy, setBusy] = useState<SiteAccessBusy>(null);
  const zone = safeTimeZone(access.state?.timeZone);

  /** Ошибка действия: текст, «Повторить» при сбое сети, перечитать при устаревшем состоянии. */
  function fail(err: unknown, action: SiteAccessAction, retry?: () => void) {
    const text = siteAccessErrorText(err, action, zone);
    if (retry && isSiteAccessNetworkError(err)) notify.warning(text, { action: { label: "Повторить", onClick: retry } });
    else notify.error(text);
    if (isSiteAccessStale(err)) void access.reload();
  }

  async function open(closureId: string): Promise<void> {
    setBusy("open");
    try {
      access.apply(await siteAccess.open(closureId));
      notify.success(`Сайт открыт для покупателей — ${SITE_ACCESS_DELAY_NOTE}`);
    } catch (err) {
      fail(err, "open", () => void open(closureId));
    } finally {
      setBusy(null);
    }
  }

  async function extend(closureId: string): Promise<void> {
    setBusy("extend");
    try {
      const state = await siteAccess.extend(closureId);
      access.apply(state);
      const auto = state.shopClosure?.autoOpenAt;
      notify.success(auto ? `Пауза продлена — сайт откроется сам ${zonedWhen(new Date(auto), access.serverNow(), zone)}` : "Пауза продлена");
    } catch (err) {
      fail(err, "extend", () => void extend(closureId));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Вызывать прямо в обработчике нажатия: вкладка открывается до запроса.
   * `path` — страница витрины, на которую вести после пропуска (по умолчанию главная).
   */
  function watch(path?: string): void {
    const tab = window.open("", "_blank");
    if (!tab) {
      notify.error("Браузер не дал открыть новую вкладку — разрешите всплывающие окна для кабинета.");
      return;
    }
    // Вкладка сайта не должна управлять кабинетом через window.opener.
    tab.opener = null;
    setBusy("watch");
    siteAccess
      .staffPass(path)
      .then((pass) => {
        if (pass.url) tab.location.href = pass.url;
        else {
          tab.close();
          notify.error("Адрес сайта не настроен — посмотреть сайт пока нельзя. Напишите в поддержку платформы.");
        }
      })
      .catch((err: unknown) => {
        tab.close();
        fail(err, "staffPass");
      })
      .finally(() => setBusy(null));
  }

  return { busy, open, extend, watch };
}
